import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import CommonService from '../../services/common.service';
import { formatCanonicalStatus } from './formatCanonicalStatus';
import '../../assets/styles/linked_logistics_panel.scss';

export default function LinkedLogisticsPanel({
  sourceModule,
  sourceRecordType,
  sourceRecordId,
  linkPurpose = 'fulfillment',
  title = 'Linked logistics',
  className = '',
  variant = 'full',
  /**
   * Optional extra query params merged into the generic-form redirect URL.
   * Used by callers that need to pass additional context the panel can't infer
   * from sourceRecordId alone — e.g. CM's `manufacturing_quote` panel needs to
   * pass the parent `cmRfqId` because sourceRecordId is the quote id.
   */
  extraInitiateParams = null,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [initiating, setInitiating] = useState(false);

  const loadSummary = useCallback(async () => {
    if (!sourceModule || !sourceRecordType || sourceRecordId == null || sourceRecordId === '') {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams({
        source_module: sourceModule,
        source_record_type: sourceRecordType,
        source_record_id: String(sourceRecordId),
        link_purpose: linkPurpose,
      });
      const resp = await CommonService.get(`/module-logistics/summary?${qs.toString()}`);
      const data = resp?.data ?? resp;
      setSummary(data && typeof data === 'object' ? data : null);
    } catch (e) {
      // 403 = caller is not a participant in this source record (e.g. browsing an auction
      // they haven't bid on yet). 404 = source record missing. 409 = stage not yet reached
      // (e.g. auction has no winning bid). In all three cases the panel is simply not
      // applicable for this user/record, so hide it instead of surfacing a red error.
      const status = e?.response?.status ?? e?.statusCode;
      if (status === 403 || status === 404 || status === 409) {
        setSummary({ widgetVisible: false });
        setError(null);
      } else {
        setError(e?.message || 'Failed to load logistics summary');
        setSummary(null);
      }
    } finally {
      setLoading(false);
    }
  }, [sourceModule, sourceRecordType, sourceRecordId, linkPurpose]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const handleInitiate = async () => {
    if (
      sourceModule === 'buyer_seller' &&
      sourceRecordType === 'seller_quote' &&
      sourceRecordId != null
    ) {
      window.location.assign(
        `/logistics/rfq/generic?sellerQuoteId=${encodeURIComponent(String(sourceRecordId))}`
      );
      return;
    }
    if (
      sourceModule === 'buyer_seller' &&
      sourceRecordType === 'sales_order' &&
      sourceRecordId != null
    ) {
      window.location.assign(
        `/logistics/rfq/generic?salesOrderId=${encodeURIComponent(String(sourceRecordId))}`
      );
      return;
    }
    if (
      sourceModule === 'auction' &&
      (sourceRecordType === 'auction_award' || sourceRecordType === 'winning_bid') &&
      sourceRecordId != null
    ) {
      const params = new URLSearchParams({
        auctionId: String(sourceRecordId),
        sourceType: sourceRecordType,
      });
      window.location.assign(`/logistics/rfq/generic?${params.toString()}`);
      return;
    }
    if (sourceModule === 'contract_manufacturing' && sourceRecordId != null && sourceRecordType) {
      // Derive cm_rfq_id from sourceRecordId for every CM source type except
      // `manufacturing_quote`, whose sourceRecordId is the quote id. For that case
      // the caller must pass `extraInitiateParams={ cmRfqId }`.
      const overrideCmRfqId = extraInitiateParams?.cmRfqId ?? extraInitiateParams?.cm_rfq_id;
      const rawId = String(sourceRecordId);
      const derivedCmRfqId =
        overrideCmRfqId != null
          ? String(overrideCmRfqId)
          : sourceRecordType === 'manufacturing_quote'
            ? ''
            : rawId.includes(':')
              ? rawId.split(':')[0]
              : rawId;
      if (derivedCmRfqId) {
        const params = new URLSearchParams({
          cmRfqId: derivedCmRfqId,
          sourceType: sourceRecordType,
          linkPurpose: linkPurpose || 'fulfillment',
          sourceRecordId: rawId,
        });
        window.location.assign(`/logistics/rfq/generic?${params.toString()}`);
        return;
      }
    }
    setInitiating(true);
    setError(null);
    try {
      await CommonService.post('/module-logistics/initiate', {
        source_module: sourceModule,
        source_record_type: sourceRecordType,
        source_record_id: String(sourceRecordId),
        link_purpose: linkPurpose,
      });
      await loadSummary();
    } catch (e) {
      setError(e?.message || 'Could not create logistics RFQ');
    } finally {
      setInitiating(false);
    }
  };

  if (!loading && summary && summary.widgetVisible === false) {
    return null;
  }

  const purposeLink =
    summary?.links?.find((l) => l.linkPurpose === linkPurpose) ?? summary?.links?.[0] ?? null;
  const needLabel =
    summary?.logisticsRequired === 'required'
      ? 'Required'
      : summary?.logisticsRequired === 'not_required'
        ? 'Not required'
        : 'Optional';

  const isCard = variant === 'card' || variant === 'compact';
  const isCompact = variant === 'compact';
  const showExtendedFields =
    !isCompact ||
    purposeLink?.ewayBillStatus === 'created' ||
    purposeLink?.shippingManifestStatus === 'created' ||
    Boolean(purposeLink?.grnStatus);

  const panelClass = [
    'linked-logistics-panel',
    isCard ? 'linked-logistics-panel--card' : '',
    isCompact ? 'linked-logistics-panel--compact' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const showStartLogistics =
    summary?.canInitiate ||
    (sourceModule === 'buyer_seller' &&
      (sourceRecordType === 'sales_order' || sourceRecordType === 'seller_quote') &&
      !purposeLink &&
      summary?.logisticsRequired === 'required');

  const isAuctionPostAward =
    sourceModule === 'auction' &&
    (sourceRecordType === 'auction_award' || sourceRecordType === 'winning_bid');
  const isContractManufacturing = sourceModule === 'contract_manufacturing';
  const startLogisticsLabel =
    sourceModule === 'buyer_seller' &&
    (sourceRecordType === 'sales_order' || sourceRecordType === 'seller_quote')
      ? 'Start Logistics RFQ'
      : isAuctionPostAward
        ? 'Start Logistics RFQ'
        : isContractManufacturing
          ? 'Start Logistics RFQ'
          : 'Create Logistics RFQ';

  return (
    <section className={panelClass} aria-label={title}>
      <header className="llp-header">
        <h3 className="llp-title">{title}</h3>
        <span className={`llp-need llp-need--${summary?.logisticsRequired || 'optional'}`}>{needLabel}</span>
      </header>

      {linkPurpose !== 'fulfillment' && (
        <p className="llp-muted llp-purpose-tag">Purpose: {linkPurpose.replace(/_/g, ' ')}</p>
      )}

      {loading && <p className="llp-muted">Loading logistics status…</p>}
      {!loading && error && <p className="llp-error">{error}</p>}

      {!loading && !error && summary && (
        <>
          {purposeLink ? (
            <dl className="llp-grid">
              <div>
                <dt>Status</dt>
                <dd>{formatCanonicalStatus(purposeLink.canonicalStatus)}</dd>
              </div>
              <div>
                <dt>RFQ ref</dt>
                <dd>{purposeLink.rfqReferenceNumber || '—'}</dd>
              </div>
              <div>
                <dt>Quotes</dt>
                <dd>{purposeLink.quoteCount ?? 0}</dd>
              </div>
              <div>
                <dt>Partner</dt>
                <dd>{purposeLink.acceptedLogisticsPartner || '—'}</dd>
              </div>
              {showExtendedFields && (
                <>
                  <div>
                    <dt>E-way bill</dt>
                    <dd>{purposeLink.ewayBillStatus || '—'}</dd>
                  </div>
                  <div>
                    <dt>Manifest</dt>
                    <dd>{purposeLink.shippingManifestStatus || '—'}</dd>
                  </div>
                  <div>
                    <dt>Delivery</dt>
                    <dd>{formatCanonicalStatus(purposeLink.deliveryMilestone) || '—'}</dd>
                  </div>
                  <div>
                    <dt>GRN</dt>
                    <dd>{purposeLink.grnStatus ? formatCanonicalStatus(purposeLink.grnStatus) : '—'}</dd>
                  </div>
                </>
              )}
              {purposeLink.syncError && (
                <div className="llp-sync-error">
                  <dt>Sync</dt>
                  <dd>{purposeLink.syncError}</dd>
                </div>
              )}
            </dl>
          ) : (
            <p className="llp-muted">
              {summary.statusHint || 'No logistics RFQ linked for this purpose yet.'}
            </p>
          )}

          <div className="llp-actions">
            {showStartLogistics && (
              <button type="button" className="llp-btn llp-btn--primary" disabled={initiating} onClick={handleInitiate}>
                {initiating ? 'Creating…' : startLogisticsLabel}
              </button>
            )}
            {purposeLink?.deepLinks?.viewInLogistics && (
              <Link to={purposeLink.deepLinks.viewInLogistics} className="llp-btn llp-btn--secondary">
                View in Logistics
              </Link>
            )}
            {summary.canCompareQuotes && purposeLink?.deepLinks?.compareQuotes && (
              <Link to={purposeLink.deepLinks.compareQuotes} className="llp-btn llp-btn--outline">
                Compare Quotes
              </Link>
            )}
          </div>

          {summary.links?.length > 1 && (
            <p className="llp-muted llp-multi">{summary.links.length} logistics links for this record.</p>
          )}
        </>
      )}
    </section>
  );
}
